import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ChevronRight, ChevronLeft, BookOpen, CheckCircle,
  Lock, Award, Clock, Home, AlertTriangle, Cloud,
  Mountain, Heart, FileText,
} from "lucide-react";
import { useAuth } from "../App";
import { toast } from "sonner";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const B = {
  border: `1px solid ${C.navy}15`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

function BCard({ children, className = "", style = {} }) {
  return (
    <div className={`bg-white ${className}`} style={{ border: B.border, boxShadow: B.shadow, borderRadius: 0, ...style }}>
      {children}
    </div>
  );
}

function BBadge({ children, bg = "#686af3", color = "#fff" }) {
  return (
    <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
      style={{ fontFamily: B.mono, background: bg, color, border: "2px solid #2D3561" }}
    >{children}</span>
  );
}

const FAMILIAS_COURSE = {
  id: "familias",
  title: "Familias",
  subtitle: "Tu plan, tus animales",
  description: "Aprende a proteger a tus animales de compañía antes, durante y después de una emergencia.",
  lessons: [
    {
      id: 1, title: "Introduccion", subtitle: "Los animales y los desastres", icon: BookOpen, duration: "20 min",
      sections: [
        { title: "Los animales son familia", content: `La mayoría de las personas consideran a sus animales como miembros de la familia. \n\nEsta relación tan estrecha significa que, durante una emergencia, los propietarios pueden ponerse en riesgo para proteger a sus animales.\n\n**Datos importantes:**\n- El 67% de los hogares españoles tienen al menos un animal de compañía\n- La perdida de un animal durante un desastre puede causar trauma psicologico significativo\n- Los niños y ancianos son especialmente vulnerables al impacto emocional\n\n**Por que este curso es importante:**\nCuidar de los animales durante los desastres facilita la seguridad personal y el bienestar de toda la familia.` },
        { title: "Tipos de emergencias", content: `Las emergencias que pueden afectar a nuestros animales incluyen:\n\n**Peligros meteorológicos:**\n- Inundaciones y DANA\n- Tormentas eléctricas\n- Olas de calor y frío extremo\n- Incendios forestales\n\n**Peligros geológicos:**\n- Terremotos\n- Tsunamis\n- Deslizamientos de tierra\n\n**Peligros tecnologicos:**\n- Vertidos químicos\n- Incidentes nucleares\n\nEn este curso aprenderemos a prepararnos para cada uno de estos escenarios.` }
      ],
      quiz: [
        { question: "¿Qué porcentaje de hogares españoles tienen animales de compañía?", options: ["45%", "67%", "80%", "30%"], correct: 1 },
        { question: "Por que es importante preparar a los animales para emergencias?", options: ["Solo por obligación legal", "Porque facilita la seguridad de toda la familia", "No es importante", "Solo para animales exoticos"], correct: 1 }
      ]
    },
    {
      id: 2, title: "Kit de emergencia", subtitle: "Prepara tu mochila de vida", icon: Home, duration: "30 min",
      sections: [
        { title: "Que debe contener tu kit", content: `El kit de emergencia para animales debe prepararse con antelación y estar siempre accesible.\n\n**Provisiones basicas (mínimo 3 días):**\n- Comida seca y/o húmeda suficiente\n- Agua embotellada (1 litro por animal/dia)\n- Cuencos plegables para comida y agua\n\n**Higiene:**\n- Bolsas para excrementos\n- Arena para gatos (si aplica)\n- Toallas de papel\n- Desinfectante\n\n**Documentación:**\n- Copia de cartilla de vacunación\n- Fotografia reciente del animal\n- Número de microchip\n- Contacto del veterinario` },
        { title: "Equipamiento adicional", content: `**Identificación y control:**\n- Collar con chapa identificativa\n- Correa y arnes extra\n- Transportin etiquetado con tus datos\n\n**Salud:**\n- Medicamentos (suministro para 2 semanas)\n- Botiquin de primeros auxilios para animales\n- Historial médico\n\n**Confort:**\n- Manta o cama pequeña\n- Juguete favorito\n- Golosinas\n\n**Consejo:** Revisa el kit cada 6 meses para verificar fechas de caducidad y actualizar documentos.` }
      ],
      quiz: [
        { question: "¿Para cuántos días mínimo debe tener provisiones el kit de emergencia?", options: ["1 día", "3 días", "1 semana", "1 mes"], correct: 1 },
        { question: "¿Cada cuánto tiempo se recomienda revisar el kit de emergencia?", options: ["Cada mes", "Cada 6 meses", "Cada ano", "Nunca"], correct: 1 }
      ]
    },
    {
      id: 3, title: "Plan de evacuación", subtitle: "Rutas y refugios seguros", icon: AlertTriangle, duration: "25 min",
      sections: [
        { title: "Planifica tu evacuación", content: `**Antes de que ocurra una emergencia:**\n\n1. **Identifica múltiples rutas de evacuación** desde tu casa\n2. **Practica la evacuación** con tus animales regularmente\n3. **Conoce los refugios** que admiten animales en tu zona\n4. **Establece puntos de encuentro** con tu familia\n\n**Opciones de refugio para animales:**\n- Casa de familiares o amigos fuera de la zona afectada\n- Hoteles que admiten animales (ten una lista preparada)\n- Residencias caninas o felinas\n- Clínicas veterinarias\n- Refugios de animales locales\n\n**Importante:** Muchos refugios para humanos NO admiten animales. Planifica con antelación.` },
        { title: "Si debes dejar a tu animal atrás", content: `**ESTO DEBE SER EL ÚLTIMO RECURSO**\n\nSi no tienes otra opción:\n- **NUNCA** dejes animales atados fuera\n- **NUNCA** los dejes sueltos sin supervision\n- Dejalos en una habitación interior segura\n- Proporciona comida y agua para varios días\n- Deja una nota visible en la puerta indicando:\n  - Número y tipo de animales\n  - Fecha en que los dejaste\n  - Tu número de contacto\n  - Necesidades especiales\n\n**Informa a alguien** (vecino, autoridades) de que hay animales en la vivienda.` }
      ],
      quiz: [
        { question: "¿Qué debes hacer con tu animal si evacúas?", options: ["Dejarlo atado fuera", "Llevarlo contigo siempre que sea posible", "Dejarlo suelto en la calle", "No hacer nada"], correct: 1 },
        { question: "¿Qué información debes dejar si no puedes llevarte a tu animal?", options: ["Ninguna", "Solo tu nombre", "Número de animales, fecha, contacto y necesidades", "Solo el nombre del animal"], correct: 2 }
      ]
    },
    {
      id: 4, title: "Identificación", subtitle: "Evita perder a tu animal", icon: FileText, duration: "20 min",
      sections: [
        { title: "Métodos de identificación", content: `La identificación adecuada es CRUCIAL para reunirte con tu animal si se pierde.\n\n**Identificación visible:**\n- Collar con chapa que incluya tu nombre y teléfono\n- El collar debe estar siempre puesto\n\n**Identificación permanente:**\n- **Microchip:** Es obligatorio en España para perros\n- Registrado en la base de datos oficial\n- Actualiza tus datos si cambias de teléfono o dirección\n\n**Identificación temporal (en emergencias):**\n- Rotulador permanente en el collar\n- Cinta adhesiva con tu teléfono\n- Foto reciente del animal contigo\n\n**Consejo:** Guarda fotos actuales de tus animales en tu teléfono y en la nube.` },
        { title: "Conoce los hábitos de tu animal", content: `Saber como reacciona tu animal al estrés puede ayudarte a encontrarlo:\n\n**Lugares donde suelen esconderse:**\n- Debajo de camas o muebles\n- En armarios o espacios cerrados\n- Detrás de electrodomésticos\n- En el jardín (arbustos, cobertizos)\n\n**Comportamientos bajo estrés:**\n- Algunos animales huyen\n- Otros se paralizan o se esconden\n- Pueden volverse agresivos por miedo\n\n**Entrenamiento recomendado:**\n- Acostumbra a tu animal al transportin\n- Practica entrar y salir del coche\n- Familiarízalo con ruidos fuertes (con cuidado)` }
      ],
      quiz: [
        { question: "¿Cuál es el método de identificación permanente más recomendado?", options: ["Collar con chapa", "Microchip", "Tatuaje", "Ninguno"], correct: 1 },
        { question: "¿Dónde suelen esconderse los gatos cuando están asustados?", options: ["En espacios abiertos", "En espacios cerrados como armarios", "En el jardín", "No se esconden"], correct: 1 }
      ]
    },
    {
      id: 5, title: "Peligros meteorológicos", subtitle: "Tormentas, inundaciones y más", icon: Cloud, duration: "35 min",
      sections: [
        { title: "Inundaciones y DANA", content: `Las inundaciones son uno de los peligros más comunes en España.\n\n**ANTES:**\n- Conoce si vives en zona inundable\n- Ten el kit de emergencia preparado\n- Identifica rutas de evacuación a zonas altas\n\n**DURANTE:**\n- Sigue las alertas de AEMET y Protección Civil\n- Evacua con tus animales antes de que sea tarde\n- **NUNCA** dejes animales atados fuera\n- **NUNCA** cruces zonas inundadas con el coche\n\n**DESPUÉS:**\n- No dejes beber a tus animales agua de inundación\n- Vigila signos de hipotermia\n- Inspecciona tu casa antes de volver con los animales\n- Consulta al veterinario si sospechas intoxicación` },
        { title: "Olas de calor y frío extremo", content: `**CALOR EXTREMO:**\n- **NUNCA** dejes a tu animal en el coche\n- Proporciona agua fresca abundante\n- Evita paseos en las horas centrales del día\n- Cuidado con el asfalto caliente (quema las almohadillas)\n- Signos de golpe de calor: jadeo excesivo, babeo, debilidad\n\n**FRIO EXTREMO:**\n- Los animales también sufren hipotermia\n- Proporciona refugio calido y seco\n- Aumenta la cantidad de comida (necesitan más calorias)\n- Cuidado con el anticongelante (es tóxico y les atrae el sabor)\n- Golpea el capo del coche antes de arrancarlo (los gatos se refugian en el motor)` }
      ],
      quiz: [
        { question: "¿Qué no debes hacer nunca durante una inundación?", options: ["Evacuar a zona alta", "Seguir las alertas oficiales", "Dejar animales atados fuera", "Preparar el kit de emergencia"], correct: 2 },
        { question: "Por que es peligroso el anticongelante para los animales?", options: ["Huele mal", "Es tóxico y les atrae el sabor dulce", "Es muy caro", "No es peligroso"], correct: 1 }
      ]
    },
    {
      id: 6, title: "Peligros geológicos", subtitle: "Terremotos, tsunamis y volcanes", icon: Mountain, duration: "30 min",
      sections: [
        { title: "Terremotos", content: `España tiene zonas con actividad sísmica, especialmente el sureste.\n\n**ANTES:**\n- Identifica lugares seguros en casa (bajo mesas solidas)\n- Asegura estanterias y objetos pesados\n- Ten el kit de emergencia accesible\n\n**DURANTE:**\n- Protegete tu primero (no podras ayudar a tu animal si estas herido)\n- Agachate, cubrete y agarrate\n- Los animales suelen buscar refugio por instinto\n\n**DESPUÉS:**\n- Busca a tu animal con calma (puede estar asustado)\n- Inspecciona la casa antes de soltar al animal\n- Vigila cristales rotos y cables eléctricos\n- Los animales pueden comportarse de forma extrana durante días\n- Mantén rutinas para reducir su estrés` },
        { title: "Tsunamis", content: `Si vives en zona costera, conoce los riesgos.\n\n**Senales de alerta:**\n- Terremoto fuerte cerca de la costa\n- El mar se retira de forma inusual\n- Alertas oficiales\n\n**QUE HACER:**\n- **EVACUA INMEDIATAMENTE** a zona alta (mínimo 30 metros sobre el nivel del mar)\n- Lleva a tus animales contigo\n- **NUNCA** dejes animales encerrados en zonas bajas\n- No vuelvas hasta que las autoridades lo indiquen\n\n**Recuerda:** Las olas de un tsunami pueden llegar en oleadas durante horas.` }
      ],
      quiz: [
        { question: "¿Cuál es la acción correcta durante un terremoto?", options: ["Salir corriendo a la calle", "Agacharse, cubrirse y agarrarse", "Quedarse de pie junto a la ventana", "Subir al tejado"], correct: 1 },
        { question: "¿Qué indica que puede venir un tsunami?", options: ["Lluvia fuerte", "El mar se retira de forma inusual", "Viento muy fuerte", "Niebla espesa"], correct: 1 }
      ]
    },
    {
      id: 7, title: "Recuperación", subtitle: "Después de la emergencia", icon: Heart, duration: "25 min",
      sections: [
        { title: "Cuidados post-emergencia", content: `Después de una emergencia, tanto tú como tus animales necesitáis tiempo para recuperaros.\n\n**Cuidados físicos:**\n- Lleva a tu animal al veterinario si sospechas lesiones\n- Ofrece comida y agua poco a poco (no en grandes cantidades)\n- Revisa almohadillas, ojos y oídos\n- Vigila signos de deshidratación o intoxicación\n\n**Cuidados emocionales:**\n- Los animales también sufren estrés post-traumatico\n- Mantén las rutinas lo más normal posible\n- Dale tiempo y espacio si lo necesita\n- Los juegos y caricias ayudan a la recuperación\n- Algunos animales pueden tener cambios de comportamiento temporales` },
        { title: "Si tu animal se ha perdido", content: `No pierdas la esperanza. Muchos animales se reencuentran con sus familias.\n\n**Acciones inmediatas:**\n- Visita los refugios locales DIARIAMENTE\n- Publica fotos en redes sociales y grupos locales\n- Coloca carteles en la zona\n- Contacta con protectoras y veterinarios cercanos\n- Registra la perdida en bases de datos oficiales\n\n**Por que la identificación es clave:**\n- Los animales con microchip tienen MUCHAS más probabilidades de volver a casa\n- Actualiza siempre tus datos de contacto en el registro\n\n**Sanación emocional:**\n- La perdida de un animal es un duelo real\n- Busca apoyo en familia, amigos o profesionales si lo necesitas` }
      ],
      quiz: [
        { question: "¿Qué debes hacer si sospechas que tu animal tiene lesiones tras una emergencia?", options: ["Esperar a ver si mejora solo", "Llevarlo al veterinario", "Darle medicamentos humanos", "No hacer nada"], correct: 1 },
        { question: "¿Cuál es la mejor forma de aumentar las probabilidades de encontrar un animal perdido?", options: ["Esperar en casa", "Publicar solo en Facebook", "Visitar refugios diariamente y publicar en múltiples canales", "No hacer nada"], correct: 2 }
      ]
    },
    {
      id: 8, title: "Resumen y certificación", subtitle: "Completa tu formación", icon: Award, duration: "15 min",
      sections: [
        { title: "Lo que has aprendido", content: `¡Felicidades! Has completado el curso de formación para familias.\n\n**Resumen de contenidos:**\n1. Por qué es importante preparar a los animales para emergencias\n2. Cómo preparar un kit de emergencia completo\n3. Planificación de evacuación con animales\n4. Métodos de identificación para evitar pérdidas\n5. Cómo actuar ante peligros meteorológicos\n6. Cómo actuar ante peligros geológicos\n7. Cuidados post-emergencia y recuperación\n\n**Recuerda:**\n- Revisa tu kit de emergencia cada 6 meses\n- Practica la evacuación con tus animales\n- Mantén la identificación actualizada\n- Conoce los refugios que admiten animales en tu zona` },
        { title: "Tu certificado", content: `Has completado todas las lecciones y superado los quizzes.\n\n**Tu certificado incluye:**\n- Tu nombre completo\n- Fecha de finalización\n- Contenidos del curso\n- Firma digital de Emergencia Animal\n\n**Puedes:**\n- Descargarlo en PDF\n- Compartirlo en redes sociales\n- Añadirlo a tu currículum\n\n**Siguiente paso:**\n¡Pon en práctica lo aprendido! Prepara tu kit de emergencia, actualiza la identificación de tus animales y comparte estos conocimientos con tu familia y amigos.\n\n*Nadie se queda atrás*` }
      ],
      quiz: [
        { question: "¿Cada cuánto debes revisar tu kit de emergencia?", options: ["Cada semana", "Cada 6 meses", "Cada 5 años", "Nunca"], correct: 1 },
        { question: "¿Qué es lo más importante que has aprendido en este curso?", options: ["Que los animales no necesitan preparación", "Que la preparación previa puede salvar vidas", "Que las emergencias no ocurren", "Que no hay nada que hacer en una emergencia"], correct: 1 }
      ]
    }
  ]
};

export default function CursoFamilias() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentLesson, setCurrentLesson] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [completedLessons, setCompletedLessons] = useState([]);
  const [progress, setProgress] = useState({});
  const [view, setView] = useState("overview");

  const lesson = FAMILIAS_COURSE.lessons[currentLesson];
  const totalLessons = FAMILIAS_COURSE.lessons.length;

  useEffect(() => { loadProgress(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProgress = async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API}/aprende/progress/familias`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("ea_token")}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCompletedLessons(data.completed_lessons || []);
        setProgress(data);
      }
    } catch { /* No previous progress */ }
  };

  const saveProgress = async (lessonId) => {
    if (!user) return;
    try {
      await fetch(`${API}/aprende/progress/familias`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("ea_token")}` },
        body: JSON.stringify({ lesson_id: lessonId, completed: true })
      });
    } catch (e) { console.error("Error saving progress", e); }
  };

  const downloadCertificate = async () => {
    try {
      const res = await fetch(`${API}/aprende/certificate/familias`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("ea_token")}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "certificado_familias.pdf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Certificado descargado!");
      } else { toast.error("Error al descargar el certificado"); }
    } catch { toast.error("Error al descargar el certificado"); }
  };

  const handleQuizSubmit = () => {
    const quiz = lesson.quiz;
    let correct = 0;
    quiz.forEach((q, idx) => { if (quizAnswers[idx] === q.correct) correct++; });
    if (correct === quiz.length) {
      toast.success("Excelente! Has superado el quiz.");
      if (!completedLessons.includes(lesson.id)) {
        setCompletedLessons([...completedLessons, lesson.id]);
        saveProgress(lesson.id);
      }
      setShowQuiz(false);
      setQuizAnswers({});
      if (currentLesson < totalLessons - 1) {
        setCurrentLesson(currentLesson + 1);
        setCurrentSection(0);
      } else {
        toast.success("¡Felicidades! Has completado el curso.");
        setView("overview");
      }
    } else {
      toast.error(`Has acertado ${correct} de ${quiz.length}. Intentalo de nuevo.`);
    }
  };

  const getProgressPercent = () => Math.round((completedLessons.length / totalLessons) * 100);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f8f7f4", fontFamily: B.font }}>
        <BCard className="p-8 text-center">
          <Lock className="w-12 h-12 mx-auto mb-4" style={{ color: "#fa9000" }} />
          <h2 className="text-xl font-bold mb-2" style={{ color: "#2D3561" }}>ACCESO REQUERIDO</h2>
          <p className="text-sm mb-4" style={{ fontFamily: B.mono, color: "#999" }}>Inicia sesión para acceder al curso</p>
          <button onClick={() => navigate("/auth")} className="px-6 py-2 font-bold text-sm uppercase tracking-wider transition-all hover:-translate-y-0.5"
            style={{ fontFamily: B.font, background: "#fa9000", color: "#fff", border: B.border, boxShadow: B.shadowSm }}>
            INICIAR SESIÓN
          </button>
        </BCard>
      </div>
    );
  }

  /* ====== OVERVIEW ====== */
  if (view === "overview") {
    return (
      <div className="min-h-screen" data-testid="curso-familias-overview" style={{ background: "#f8f7f4", fontFamily: B.font }}>
        {/* Header */}
        <header className="sticky top-0 z-50" style={{ background: "#2D3561", borderBottom: B.border }}>
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/aprende" className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider hover:opacity-80" style={{ fontFamily: B.mono, color: "#f8f7f4" }}>
              <ArrowLeft className="w-4 h-4" /> APRENDE
            </Link>
            <BBadge bg="#fa9000">{getProgressPercent()}% COMPLETADO</BBadge>
          </div>
        </header>

        {/* Course Header */}
        <section className="py-12 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 flex items-center justify-center" style={{ background: "#fa9000", border: B.border }}>
                <Home className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold" style={{ color: "#2D3561" }}>{FAMILIAS_COURSE.title}</h1>
                <p className="text-sm tracking-wider" style={{ fontFamily: B.mono, color: "#fa9000" }}>{FAMILIAS_COURSE.subtitle}</p>
              </div>
            </div>
            <p className="text-sm mb-6" style={{ color: "#666" }}>{FAMILIAS_COURSE.description}</p>

            {/* Progress Bar */}
            <div className="mb-2 h-3" style={{ background: "#fff", border: "2px solid #2D3561" }}>
              <div className="h-full transition-all" style={{ width: `${getProgressPercent()}%`, background: "#fa9000" }} />
            </div>
            <p className="text-[10px] tracking-wider mb-6" style={{ fontFamily: B.mono, color: "#999" }}>
              {completedLessons.length} DE {totalLessons} LECCIONES COMPLETADAS
            </p>

            {/* Certificate */}
            {completedLessons.length >= totalLessons && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                <BCard className="p-6 mb-8" style={{ background: "#fa900010", boxShadow: "none" }}>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 flex items-center justify-center" style={{ background: "#fa9000", border: B.border }}>
                      <Award className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg" style={{ color: "#2D3561" }}>ENHORABUENA!</h3>
                      <p className="text-xs" style={{ fontFamily: B.mono, color: "#999" }}>Has completado el curso. Tu certificado está listo.</p>
                    </div>
                    <button onClick={downloadCertificate} className="px-5 py-2.5 font-bold text-sm uppercase tracking-wider transition-all hover:-translate-y-0.5 flex items-center gap-2"
                      style={{ fontFamily: B.font, background: "#fa9000", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
                      data-testid="download-certificate-btn">
                      <Award className="w-4 h-4" /> CERTIFICADO
                    </button>
                  </div>
                </BCard>
              </motion.div>
            )}
          </div>
        </section>

        {/* Lessons List */}
        <section className="pb-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold tracking-tight mb-6 flex items-center gap-2" style={{ color: "#2D3561" }}>
              <div className="w-8 h-8 flex items-center justify-center" style={{ background: "#fa9000", border: "2px solid #2D3561" }}>
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              LECCIONES
            </h2>
            <div className="space-y-4">
              {FAMILIAS_COURSE.lessons.map((l, idx) => {
                const isCompleted = completedLessons.includes(l.id);
                const isLocked = idx > 0 && !completedLessons.includes(FAMILIAS_COURSE.lessons[idx - 1].id);
                const Icon = l.icon;
                return (
                  <motion.div key={l.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}>
                    <BCard
                      className={`p-4 flex items-center gap-4 cursor-pointer transition-all hover:-translate-y-0.5 ${isLocked && !isCompleted ? "opacity-40 cursor-not-allowed" : ""}`}
                      style={{ boxShadow: isCompleted ? "4px 4px 0px #8B9A2D" : B.shadowSm }}
                      onClick={() => {
                        if (!isLocked || isCompleted) { setCurrentLesson(idx); setCurrentSection(0); setShowQuiz(false); setView("lesson"); }
                      }}
                    >
                      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                        style={{ background: isCompleted ? "#8B9A2D" : isLocked ? "#ccc" : "#fa9000", border: "2px solid #2D3561" }}>
                        {isCompleted ? <CheckCircle className="w-6 h-6 text-white" /> : isLocked ? <Lock className="w-6 h-6 text-white" /> : <Icon className="w-6 h-6 text-white" />}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold" style={{ color: "#2D3561" }}>
                          LECCIÓN {l.id}: {l.title}
                        </h3>
                        <p className="text-xs" style={{ fontFamily: B.mono, color: "#999" }}>{l.subtitle}</p>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] tracking-wider flex-shrink-0" style={{ fontFamily: B.mono, color: "#999" }}>
                        <Clock className="w-3.5 h-3.5" /> {l.duration}
                      </div>
                      <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: isLocked ? "#ccc" : "#2D3561" }} />
                    </BCard>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    );
  }

  /* ====== LESSON VIEW ====== */
  return (
    <div className="min-h-screen" data-testid="curso-familias-lesson" style={{ background: "#f8f7f4", fontFamily: B.font }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ background: "#2D3561", borderBottom: B.border }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => setView("overview")} className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider hover:opacity-80"
            style={{ fontFamily: B.mono, color: "#f8f7f4" }}>
            <ArrowLeft className="w-4 h-4" /> VOLVER AL CURSO
          </button>
          <BBadge bg="#fa9000">LECCIÓN {lesson.id} / {totalLessons}</BBadge>
        </div>
      </header>

      <section className="py-8 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Lesson Title */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 flex items-center justify-center" style={{ background: "#fa9000", border: B.border }}>
              <lesson.icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: "#2D3561" }}>{lesson.title}</h1>
              <p className="text-xs tracking-wider" style={{ fontFamily: B.mono, color: "#fa9000" }}>{lesson.subtitle}</p>
            </div>
          </div>

          {!showQuiz ? (
            <>
              {/* Section Content */}
              <BCard className="p-6 mb-6">
                <h2 className="text-lg font-bold mb-4" style={{ color: "#2D3561" }}>
                  {lesson.sections[currentSection].title}
                </h2>
                <div>
                  {lesson.sections[currentSection].content.split("\n\n").map((paragraph, idx) => (
                    <p key={idx} className="mb-4 text-sm leading-relaxed whitespace-pre-line" style={{ color: "#555" }}>
                      {paragraph.split("**").map((part, i) =>
                        i % 2 === 1 ? <strong key={i} style={{ color: "#2D3561" }}>{part}</strong> : part
                      )}
                    </p>
                  ))}
                </div>
              </BCard>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    if (currentSection > 0) setCurrentSection(currentSection - 1);
                    else if (currentLesson > 0) { setCurrentLesson(currentLesson - 1); setCurrentSection(FAMILIAS_COURSE.lessons[currentLesson - 1].sections.length - 1); }
                  }}
                  disabled={currentLesson === 0 && currentSection === 0}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 flex items-center gap-1.5 disabled:opacity-30"
                  style={{ fontFamily: B.mono, background: "#fff", color: "#2D3561", border: B.border }}
                >
                  <ChevronLeft className="w-4 h-4" /> ANTERIOR
                </button>
                <div className="flex gap-2">
                  {lesson.sections.map((_, idx) => (
                    <div key={idx} className="w-3 h-3" style={{ background: idx === currentSection ? "#fa9000" : "#ccc", border: "2px solid #2D3561" }} />
                  ))}
                </div>
                <button
                  onClick={() => {
                    if (currentSection < lesson.sections.length - 1) setCurrentSection(currentSection + 1);
                    else setShowQuiz(true);
                  }}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 flex items-center gap-1.5"
                  style={{ fontFamily: B.mono, background: "#fa9000", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
                  data-testid="next-section-btn"
                >
                  {currentSection < lesson.sections.length - 1 ? "SIGUIENTE" : "IR AL QUIZ"}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            /* Quiz */
            <BCard className="p-6">
              <h2 className="text-lg font-bold mb-6 flex items-center gap-2" style={{ color: "#2D3561" }}>
                <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#fa9000", border: "2px solid #2D3561" }}>
                  <Award className="w-4 h-4 text-white" />
                </div>
                QUIZ DE LA LECCIÓN
              </h2>
              <div className="space-y-6">
                {lesson.quiz.map((q, idx) => (
                  <div key={idx} className="pb-4" style={{ borderBottom: idx < lesson.quiz.length - 1 ? "2px solid #f8f7f4" : "none" }}>
                    <p className="font-bold mb-3" style={{ color: "#2D3561" }}>{idx + 1}. {q.question}</p>
                    <div className="space-y-2">
                      {q.options.map((option, optIdx) => (
                        <label key={optIdx}
                          className="flex items-center gap-3 p-3 cursor-pointer transition-all"
                          style={{
                            border: quizAnswers[idx] === optIdx ? "3px solid #fa9000" : "2px solid #ccc",
                            background: quizAnswers[idx] === optIdx ? "#fa900010" : "#fff",
                          }}
                          onClick={() => setQuizAnswers({ ...quizAnswers, [idx]: optIdx })}
                        >
                          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"
                            style={{ border: quizAnswers[idx] === optIdx ? "3px solid #fa9000" : "2px solid #ccc" }}>
                            {quizAnswers[idx] === optIdx && <div className="w-2 h-2" style={{ background: "#fa9000" }} />}
                          </div>
                          <span className="text-sm" style={{ color: "#555" }}>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-6">
                <button onClick={() => setShowQuiz(false)}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5"
                  style={{ fontFamily: B.mono, background: "#fff", color: "#2D3561", border: B.border }}>
                  VOLVER AL CONTENIDO
                </button>
                <button onClick={handleQuizSubmit}
                  disabled={Object.keys(quizAnswers).length !== lesson.quiz.length}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 disabled:opacity-30"
                  style={{ fontFamily: B.mono, background: "#fa9000", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
                  data-testid="submit-quiz-btn">
                  COMPROBAR RESPUESTAS
                </button>
              </div>
            </BCard>
          )}
        </div>
      </section>
    </div>
  );
}
